import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiX, FiShield, FiCopy, FiCheck, FiLoader, FiSmartphone,
  FiArrowLeft, FiChevronRight, FiCheckCircle
} from 'react-icons/fi';
import QRCode from 'qrcode';
import SafeIcon from './SafeIcon';
import { supabase } from '@/integrations/supabase/client';

/**
 * 2FA setup wizard — Terminal V3 aesthetic.
 * Three steps: scan QR → verify 6-digit code → confirmation.
 * Uses the existing verify-totp Edge Function + enable_totp() RPC.
 */

const TwoFactorSetup = ({ isOpen, onClose, userEmail, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [secret, setSecret] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen && !secret) generateSecret();
    if (!isOpen) {
      // Reset on close so re-opening starts fresh.
      setStep(1);
      setSecret('');
      setQrCodeUrl('');
      setVerificationCode('');
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const generateSecret = async () => {
    try {
      setLoading(true);
      setError('');
      const { data, error } = await supabase.functions.invoke('verify-totp', {
        body: { action: 'generate', email: userEmail },
      });
      if (error) throw error;
      if (!data.success) throw new Error(data.error);

      setSecret(data.secret);

      const qrDataUrl = await QRCode.toDataURL(data.otpAuthUrl, {
        width: 240,
        margin: 1,
        color: { dark: '#FFFFFF', light: '#00000000' },
      });
      setQrCodeUrl(qrDataUrl);
    } catch (err) {
      console.error('Error generating secret:', err);
      setError('Failed to generate 2FA secret. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const verifyAndEnable = async () => {
    if (verificationCode.length !== 6) {
      setError('Please enter the 6-digit code.');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const { data: verifyData, error: verifyError } = await supabase.functions.invoke('verify-totp', {
        body: { action: 'verify', secret, code: verificationCode },
      });
      if (verifyError) throw verifyError;
      if (!verifyData.success || !verifyData.valid) {
        setError('Invalid code. Try again with a fresh one.');
        return;
      }
      const { error: enableError } = await supabase.rpc('enable_totp', { p_secret: secret });
      if (enableError) throw enableError;

      setStep(3);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Error enabling 2FA:', err);
      setError('Failed to enable 2FA. Try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const StepDot = ({ n, active, done }) => (
    <div className="flex items-center gap-1.5">
      <span className={`w-1.5 h-1.5 rounded-full transition-all ${
        done ? 'bg-green-400' : active ? 'bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.6)]' : 'bg-gray-700'
      }`} />
      <span className={`font-mono text-[10px] uppercase tracking-widest ${
        active || done ? 'text-white' : 'text-gray-600'
      }`}>
        {n}
      </span>
    </div>
  );

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.96, opacity: 0 }}
          className="bg-gray-950 border border-white/10 rounded-3xl w-full max-w-md overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative">
            <div className="pointer-events-none absolute inset-0 opacity-[0.05] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:32px_32px]" />

            {/* Header */}
            <div className="relative px-6 pt-6 pb-4 flex items-start justify-between">
              <div className="min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-2">
                  ▸ Two-factor setup
                </div>
                <h3 className="text-xl font-black text-white leading-tight">
                  {step === 1 && 'Scan the code.'}
                  {step === 2 && 'Enter 6 digits.'}
                  {step === 3 && 'You are protected.'}
                </h3>
                <div className="flex items-center gap-3 mt-3">
                  <StepDot n="01 · scan"    active={step === 1} done={step > 1} />
                  <span className="w-4 h-px bg-white/10" />
                  <StepDot n="02 · verify"  active={step === 2} done={step > 2} />
                  <span className="w-4 h-px bg-white/10" />
                  <StepDot n="03 · done"    active={step === 3} done={false} />
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-xl border border-white/10 text-gray-500 hover:text-white hover:border-white/20 transition-all"
              >
                <SafeIcon icon={FiX} className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="relative px-6 pb-6">
              {error && (
                <div className="mb-4 px-4 py-2.5 rounded-xl border bg-red-500/5 border-red-500/30 text-red-300 font-mono text-xs">
                  ✗ {error}
                </div>
              )}

              {/* Step 1 — QR code + secret */}
              {step === 1 && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-400 leading-relaxed">
                    Open <span className="text-white font-medium">Google Authenticator</span>, <span className="text-white font-medium">1Password</span>, or <span className="text-white font-medium">Authy</span> on your phone.
                    Scan the QR code below, or enter the key manually.
                  </p>

                  {loading ? (
                    <div className="flex items-center justify-center py-12 rounded-2xl border border-white/10 bg-black/40">
                      <SafeIcon icon={FiLoader} className="w-6 h-6 animate-spin text-cyan-400" />
                    </div>
                  ) : (
                    <div className="p-5 rounded-2xl border border-white/10 bg-black/40 flex items-center justify-center">
                      {qrCodeUrl && <img src={qrCodeUrl} alt="2FA QR code" className="w-52 h-52" />}
                    </div>
                  )}

                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
                      Secret key (manual entry)
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={secret}
                        readOnly
                        className="flex-1 bg-black/60 border border-white/10 rounded-xl px-3 py-2.5 text-white font-mono text-sm tracking-wider focus:outline-none"
                      />
                      <button
                        onClick={copySecret}
                        title="Copy secret"
                        className="px-3 rounded-xl border border-white/10 text-gray-300 hover:text-white hover:border-white/20 transition-colors"
                      >
                        <SafeIcon icon={copied ? FiCheck : FiCopy} className={`w-4 h-4 ${copied ? 'text-green-400' : ''}`} />
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={() => setStep(2)}
                    disabled={loading || !secret}
                    className="w-full inline-flex justify-center items-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-cyan-400 to-purple-400 text-black text-sm font-bold shadow-lg shadow-cyan-500/20 hover:scale-[1.01] transition-transform disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    Next: enter code <SafeIcon icon={FiChevronRight} className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Step 2 — verify code */}
              {step === 2 && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-400 leading-relaxed">
                    Type the 6-digit code your Authenticator app is showing right now. Codes rotate every 30 seconds.
                  </p>

                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
                      Verification code
                    </div>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoFocus
                      maxLength={6}
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="123456"
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-4 text-white text-center text-3xl font-mono tracking-[0.5em] placeholder-gray-700 focus:outline-none focus:border-cyan-400/50 transition-colors"
                    />
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => setStep(1)}
                      className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-white/10 text-gray-300 hover:text-white hover:border-white/20 text-sm transition-colors"
                    >
                      <SafeIcon icon={FiArrowLeft} className="w-3.5 h-3.5" />
                      Back
                    </button>
                    <button
                      onClick={verifyAndEnable}
                      disabled={loading || verificationCode.length !== 6}
                      className="flex-1 inline-flex justify-center items-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-cyan-400 to-purple-400 text-black text-sm font-bold shadow-lg shadow-cyan-500/20 hover:scale-[1.01] transition-transform disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
                    >
                      {loading ? (
                        <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                      ) : (
                        <>Verify &amp; enable <FiChevronRight className="w-4 h-4" /></>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3 — success */}
              {step === 3 && (
                <div className="text-center space-y-4 py-2">
                  <div className="w-16 h-16 mx-auto rounded-full border border-green-500/30 bg-green-500/10 flex items-center justify-center">
                    <SafeIcon icon={FiCheckCircle} className="w-8 h-8 text-green-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-white">2FA is now active.</h4>
                    <p className="text-sm text-gray-400 mt-2 leading-relaxed">
                      You will need your Authenticator app on each new login.
                      <br />
                      <span className="text-cyan-400">Generate recovery codes next</span> so you don&apos;t get locked out if you lose your phone.
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="w-full inline-flex justify-center items-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-cyan-400 to-purple-400 text-black text-sm font-bold shadow-lg shadow-cyan-500/20 hover:scale-[1.01] transition-transform"
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TwoFactorSetup;
